import classNames from 'classnames';
import { DateTime } from 'luxon';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import useSWR from 'swr';

import { swrFetcher } from 'utils/api';

import { ArrowRightIcon } from '@heroicons/react/20/solid';

import Footer from 'components/Footer';
import MaxWidthContainer from 'components/MaxWidthContainer';
import Menubar from 'components/Menubar';
import MobileTabBar from 'components/MobileTabBar';
import StrapiImg from 'components/StrapiImg';

import { useAppLaunch } from 'contexts/AppLaunch';
import { useUserAuth } from 'contexts/UserAuth';

import 'react-loading-skeleton/dist/skeleton.css';

const { useEffect } = React;

const Campaign = (props) => {
  const {
    isLoading: isAppLaunchLoading,
    firstCategoryMap,
    secondCategoryMap,
    activityTagMap,
    notifications,
  } = useAppLaunch();

  const { user, token } = useUserAuth();
  const skeleton = Array(3).fill(0);

  const {
    data: activities,
    error,
    isLoading: isActivitiesLoading,
    isValidating,
    mutate,
  } = useSWR('/api/v3/activities', (url) => swrFetcher(url, token));

  useEffect(() => {
    mutate();
  }, [token]);

  return (
    <div>
      <NextSeo
        title="活動專區｜最新美妝試用及會員專屬禮遇情報 | Sorra"
        description="探索Sorra的活動專區，掌握限時美妝產品試用及會員專屬禮遇等最新活動情報！"
      />
      {!isAppLaunchLoading && (
        <Menubar
          props={{
            homeExtend: false,
            notifications: notifications,
            sourceUI: 'Campaign',
          }}
        />
      )}
      <div
        className={
          'globals-centerContentsMargin relative' +
          (notifications && notifications.length > 0 ? ' withNoti' : '')
        }
      >
        <MaxWidthContainer>
          <div className="px-4 pt-4 lg:px-0 lg:pt-8">
            <p className="text-3xl font-bold lg:text-5xl">活動</p>
            {isActivitiesLoading &&
              skeleton.map((_, idx, arr) => {
                return (
                  <div key={idx} className="pt-4 lg:pt-12">
                    <div className="flex flex-col items-center gap-x-10 gap-y-5 lg:flex-row">
                      <div className="relative aspect-[343/193] w-full shrink-0 overflow-hidden rounded-lg lg:w-[343px]">
                        <Skeleton className="h-full w-full" />
                      </div>
                      <div className="flex w-full min-w-0 flex-col lg:w-auto lg:grow">
                        <Skeleton className="text-2xl" />
                        <div className="pt-4">
                          <Skeleton className="text-base" count={3} />
                        </div>
                        <div
                          className={classNames(
                            'mt-4 w-full bg-gray-200 lg:mt-11',
                            {
                              'h-px': idx < arr.length - 1,
                              'h-0': idx === arr.length - 1,
                            }
                          )}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}

            {(activities || []).map((activity, idx, arr) => {
              const name = _.get(activity, 'name');
              const title = _.get(activity, 'title');
              const excerpt = _.get(activity, 'excerpt');
              const thumbnail = _.get(activity, 'thumbnail.url');
              const tags = _.get(activity, 'tags', []);
              const date = DateTime.fromISO(activity.date);
              const appOnly = _.get(activity, 'app_only', false);
              const tagObjs = tags.map((tagId) => activityTagMap[tagId] || {});
              if (appOnly) {
                tagObjs.push({
                  name: 'App限定',
                  text_color: '#FF6079',
                  background_color: '#FFE0E5',
                });
              }
              return (
                <div key={activity.id} className="pt-4 lg:pt-12">
                  <Link
                    href={`/campaign/${name}`}
                    className="flex flex-col items-center gap-x-10 gap-y-5 lg:flex-row"
                  >
                    <div className="relative aspect-[343/193] w-full shrink-0 overflow-hidden rounded-lg lg:w-[343px]">
                      <StrapiImg
                        src={thumbnail}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="flex w-full min-w-0 flex-col lg:w-auto lg:grow">
                      <div className="flex gap-x-2">
                        {tagObjs.map((activityTag, idx) => {
                          const { name, text_color, background_color } =
                            activityTag;
                          const icon =
                            name == '免費體驗'
                              ? 'speakerphone'
                              : name == 'App限定'
                                ? 'badge-check'
                                : 'sparkles';
                          return (
                            <div
                              key={idx}
                              className="flex flex-row items-center gap-x-1 rounded-md px-2.5 py-0.5"
                              style={{ backgroundColor: background_color }}
                            >
                              <Image
                                alt="activity-tag"
                                src={`/images/icons/${icon}.png`}
                                style={{ objectFit: 'contain' }}
                                width={14}
                                height={14}
                              />
                              <p
                                className="text-xs font-medium"
                                style={{ color: text_color }}
                              >
                                {name}
                              </p>
                            </div>
                          );
                        })}
                        <div className="grow" />
                        <p className="text-sm text-gray-500">
                          {date.toFormat('dd/MM/yyyy')}
                        </p>
                      </div>
                      <div className="pt-5">
                        <p className="text-2xl font-bold text-gray-900">
                          {title}
                        </p>
                        <p className="whitespace-pre-line pt-4 text-base text-gray-500">
                          {excerpt}
                        </p>
                        <div className="flex flex-row items-center gap-x-1.5 pt-4 text-sorra-pink">
                          <p className="text-base font-semibold">立即參加</p>
                          <div className="">
                            <ArrowRightIcon className="h-4 w-4" />
                          </div>
                        </div>
                        <div
                          className={classNames(
                            'mt-4 w-full bg-gray-200 lg:mt-11',
                            {
                              'h-px': idx < arr.length - 1,
                              'h-0': idx === arr.length - 1,
                            }
                          )}
                        ></div>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </MaxWidthContainer>
        <div className="pt-6 lg:pt-24"></div>
        <Footer />
        <MobileTabBar props={{ highlighted: 'campaign' }} />
      </div>
    </div>
  );
};

export default Campaign;
